import { supabase } from "@/integrations/supabase/client";
import { enrichSpecificBook } from "@/lib/enrichmentUtils";

/**
 * Clear covers and trigger re-enrichment for books with foreign language covers
 */
export async function clearAndReEnrichCovers(bookIds: string[]) {
  try {
    console.log(`[FixCovers] Clearing covers for ${bookIds.length} books`);
    
    // Clear the cover URLs to force re-enrichment
    const { error: updateError } = await supabase
      .from('books')
      .update({ 
        cover_url: null,
        cover_cache_url: null,
        cover_cached: false
      })
      .in('id', bookIds);

    if (updateError) {
      console.error('[FixCovers] Error clearing covers:', updateError);
      throw updateError;
    }

    console.log('[FixCovers] Covers cleared, triggering re-enrichment...');
    
    // Trigger enrichment for each book
    const enrichmentResults = await Promise.allSettled(
      bookIds.map(bookId => enrichSpecificBook(bookId))
    );

    const successful = enrichmentResults.filter(r => r.status === 'fulfilled').length;
    const failed = enrichmentResults.filter(r => r.status === 'rejected').length;

    console.log(`[FixCovers] Re-enrichment complete: ${successful} successful, ${failed} failed`);
    
    return {
      cleared: bookIds.length,
      enriched: successful,
      failed
    };
  } catch (error) {
    console.error('[FixCovers] Error in clearAndReEnrichCovers:', error);
    throw error;
  }
}

/**
 * Fix ALL books with foreign language covers
 * @param batchSize Number of books to process in each batch
 */
export async function fixAllForeignCovers(batchSize: number = 50) {
  try {
    console.log('[FixCovers] Finding all books with non-English language codes...');
    
    // Query for books that:
    // 1. Have a cover URL
    // 2. Either have language != 'en' OR language is null (unknown)
    const { data: books, error } = await supabase
      .from('books')
      .select('id, title, language, cover_url')
      .not('cover_url', 'is', null)
      .or('language.neq.en,language.is.null');

    if (error) {
      console.error('[FixCovers] Error fetching books:', error);
      throw error;
    }

    if (!books || books.length === 0) {
      console.log('[FixCovers] No books with foreign covers found');
      return { 
        totalFound: 0, 
        cleared: 0, 
        enriched: 0, 
        failed: 0,
        batches: 0
      };
    }

    console.log(`[FixCovers] Found ${books.length} books with foreign/unknown language covers`);
    
    // Process in batches
    const totalBatches = Math.ceil(books.length / batchSize);
    let totalCleared = 0;
    let totalEnriched = 0;
    let totalFailed = 0;

    for (let i = 0; i < totalBatches; i++) {
      const batchStart = i * batchSize;
      const batchEnd = Math.min((i + 1) * batchSize, books.length);
      const batch = books.slice(batchStart, batchEnd);
      const bookIds = batch.map(b => b.id);
      
      console.log(`[FixCovers] Processing batch ${i + 1}/${totalBatches} (${bookIds.length} books)`);
      
      try {
        const result = await clearAndReEnrichCovers(bookIds);
        totalCleared += result.cleared;
        totalEnriched += result.enriched;
        totalFailed += result.failed;
        
        // Small delay between batches to avoid overwhelming the system
        if (i < totalBatches - 1) {
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      } catch (batchError) {
        console.error(`[FixCovers] Error processing batch ${i + 1}:`, batchError);
        totalFailed += bookIds.length;
      }
    }

    console.log(`[FixCovers] Complete! Cleared: ${totalCleared}, Enriched: ${totalEnriched}, Failed: ${totalFailed}`);
    
    return {
      totalFound: books.length,
      cleared: totalCleared,
      enriched: totalEnriched,
      failed: totalFailed,
      batches: totalBatches
    };
  } catch (error) {
    console.error('[FixCovers] Error in fixAllForeignCovers:', error);
    throw error;
  }
}

/**
 * Fix Sarah J Maas books with foreign covers
 */
export async function fixSarahJMaasCovers() {
  const { data: books, error } = await supabase
    .from('books')
    .select('id, title, cover_url')
    .or('title.ilike.%court of thorns%,title.ilike.%throne of glass%,authors.cs.{Sarah J. Maas}')
    .not('cover_url', 'is', null);

  if (error) {
    console.error('[FixCovers] Error fetching books:', error);
    throw error;
  }

  if (!books || books.length === 0) {
    console.log('[FixCovers] No books found to fix');
    return { cleared: 0, enriched: 0, failed: 0 };
  }

  const bookIds = books.map(b => b.id);
  console.log(`[FixCovers] Found ${bookIds.length} Sarah J Maas books to re-enrich`);
  
  return clearAndReEnrichCovers(bookIds);
}
